// ─────────────────────────────────────────────
//  CrossFacilityPicker — add staff from another facility for the day (D-09)
//
//  Flow: role picker (CRNA or Doctor) → staff list from other facilities
//        filtered to that role, excluding today's rostered ids and current-
//        facility members → source-facility select per row → Add.
//
//  Persisted shape (unchanged from original):
//  { id, name, role, subRole, crossFacility: true, facilityId, facilityName }
//  Reports read name + role + source facility from this record.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import tokens from "../theme/tokens";
import Modal from "./Modal";
import Button from "./Button";
import {
  listFacilityRoster,
  setDailyRoster,
  listAccountsWithoutStaff,
  addStaff as createStaffRecord,
} from "../firebase/firestore";
import { staffRoleForProfile } from "../domain/staff";
import { otherFacilities, facilityLabel as domainFacilityLabel } from "../domain/facilities";
import { shiftCodeOptions, callOrderOptions } from "../domain/schedule";

const { color, font, space, radius } = tokens;

const SHIFT_OPTIONS = shiftCodeOptions();
const CALL_OPTIONS = callOrderOptions(30);

// ── Main component ────────────────────────────────────────────────────────────
export default function CrossFacilityPicker({
  isOpen,
  onClose,
  currentFacilityId,
  date,
  actor,
  roster = { supervisors: [], crnas: [] },
  // This facility's full staff pool (board pool). Used to also offer same-
  // facility people who simply aren't on today's roster yet.
  localPool = [],
  // facilityOptions kept for backward compat but ignored — domain list is used instead
  facilityOptions: _ignored,
  // Phase 5.4 — per-facility float permission, resolved by the caller from
  // facility.rules (resolveFacilityRules). Admin callers pass true to bypass.
  floatAllowed = true,
}) {
  // Step 1: role — null | "provider" | "anesthesiologist"
  const [selectedRole, setSelectedRole] = useState(null);
  // Step 2: loaded staff from all other facilities
  const [staff, setStaff] = useState([]); // [{ ...user, _foundAtId }]
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  // Track added ids
  const [addedIds, setAddedIds] = useState([]);
  const [busyId, setBusyId] = useState(null);
  // Step 2 UX: type to filter, then pick ONE person to reveal the add detail.
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // the person being added
  const [sourceFacilityId, setSourceFacilityId] = useState("");
  const [extra, setExtra] = useState(""); // call number (doctor) / leave time (CRNA)

  // IDs already rostered today — prevent duplicates.
  const rosteredIds = [
    ...(roster.supervisors || []).map((p) => p.id),
    ...(roster.crnas || []).map((p) => p.id),
    ...addedIds,
  ];

  const clearStep2 = () => { setSearch(""); setSelected(null); setExtra(""); setSourceFacilityId(""); };

  const handleClose = () => {
    setSelectedRole(null);
    setStaff([]);
    setLoadError("");
    setAddedIds([]);
    clearStep2();
    onClose();
  };

  // Pick one person → reveal the add detail (source facility + leave/call).
  const pickPerson = (person) => {
    setSelected(person);
    setExtra("");
    const others = otherFacilities(currentFacilityId);
    setSourceFacilityId(person._local ? currentFacilityId : (person._foundAtId || others[0]?.id || ""));
  };

  const pickRole = async (role) => {
    setSelectedRole(role);
    setStaff([]);
    clearStep2();
    setLoadError("");
    setLoading(true);
    try {
      const facilities = otherFacilities(currentFacilityId);
      // Load all other facilities in parallel; tag each person with where they were found.
      const results = await Promise.all(
        facilities.map((f) =>
          listFacilityRoster(f.id).then((list) =>
            list.map((u) => ({ ...u, _foundAtId: f.id }))
          )
        )
      );
      // Flatten; dedupe by id (prefer first occurrence — usually the primary facility).
      const seen = new Set();
      const flat = [];
      for (const chunk of results) {
        for (const person of chunk) {
          if (!seen.has(person.id)) {
            seen.add(person.id);
            flat.push(person);
          }
        }
      }
      // Exclude anyone whose facilityIds includes the current facility (already in local pool).
      const filtered = flat.filter(
        (u) => !Array.isArray(u.facilityIds) || !u.facilityIds.includes(currentFacilityId)
      );
      // Filter to chosen role and sort by name.
      const roleFiltered =
        role === "anesthesiologist"
          ? filtered.filter((u) => u.role === "anesthesiologist")
          : filtered.filter((u) => u.role === "provider");

      // Same-facility staff not on today's roster (board pool), tagged _local.
      const localRole = (localPool || []).filter((p) => p && p.role === role);
      const local = localRole.map((p) => ({ ...p, _local: true, _foundAtId: currentFacilityId }));

      // Local first, then other facilities; dedupe by id (local wins).
      const seenId = new Set();
      const combined = [];
      for (const person of [...local, ...roleFiltered]) {
        if (!person || seenId.has(person.id)) continue;
        seenId.add(person.id);
        combined.push(person);
      }
      // ⚠️ ACCOUNTS THAT ARE NOT YET PEOPLE. The owner's rule is "all users are
      // staff", and the data disagreed: creating a login never created the
      // directory record, so a clinician could sign in and be invisible here —
      // which is exactly the doctor who could not be put on the board on
      // 2026-09-11. Offer them, and create the record when one is picked.
      //
      // ⚠️ FAILS SOFT, ALWAYS. Listing `users` is admin-only (the rule's
      // per-document read cannot be satisfied by a list), so this throws for
      // every doctor and CRNA. A throw means "nothing extra to offer" — never an
      // error — because this same call once broke the whole dialog for every
      // clinician and reported it as a network fault.
      let accountOnly = [];
      try {
        const accounts = await listAccountsWithoutStaff(currentFacilityId);
        accountOnly = accounts
          .filter((u) => {
            const staffRole = staffRoleForProfile({ role: u.role, subRole: u.subRole });
            if (!staffRole) return false; // admin, scheduler, observer, board device
            return role === "anesthesiologist"
              ? u.role === "anesthesiologist"
              : u.role === "provider";
          })
          .filter((u) => !seenId.has(u.id))
          .map((u) => ({
            id: u.id,
            name: u.name || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email,
            email: u.email || "",
            role: u.role,
            subRole: u.subRole || null,
            _local: true,
            _accountOnly: true,
            _foundAtId: currentFacilityId,
          }));
      } catch {
        accountOnly = [];
      }
      for (const person of accountOnly) {
        if (seenId.has(person.id)) continue;
        seenId.add(person.id);
        combined.push(person);
      }

      combined.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setStaff(combined);
    } catch (err) {
      console.error("Load cross-facility staff failed:", err);
      // ⚠️ A REFUSAL IS NOT A DROPPED CONNECTION. This message told clinicians to
      // check their wifi for eight days while the server was denying the read —
      // the whole feature was admin-only because it queried `users`. Say which
      // one it is, so the next person debugs the right thing.
      setLoadError(
        String(err?.code || "").includes("permission-denied")
          ? "You do not have permission to see other campuses' staff. Ask an administrator."
          : "Couldn't load the roster. Check your connection and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const addStaff = async (person, sourceFacilityId, extra) => {
    setBusyId(person.id);
    try {
      const isDoctor = person.role === "anesthesiologist";
      const isLocal = !!person._local; // same-facility, not on roster yet

      // ⚠️ AN ACCOUNT BECOMES A PERSON HERE, AND THE ROSTER GETS THE **STAFF**
      // ID. `roster.supervisors[].id` and `roster.crnas[].id` are staff ids —
      // every `ahpId`, `attendingId` and break lookup downstream resolves
      // against `staff`. Writing the auth uid into that field is the exact
      // defect that made this dialog write rows nothing could match.
      //
      // Linked by `uid` at birth: that link is what lets a directive, an alert
      // and a push reach them later. A staff record with `uid: null` is
      // pool-only — rosterable, but it can never be told anything.
      let memberId = person.id;
      if (person._accountOnly) {
        const staffRole = staffRoleForProfile({ role: person.role, subRole: person.subRole });
        if (!staffRole) throw new Error("That account is not a rosterable role.");
        const ref = await createStaffRecord({
          name: person.name || "",
          email: person.email || "",
          uid: person.id, // the users doc id IS the auth uid
          role: staffRole,
          primaryFacilityId: currentFacilityId,
        });
        memberId = ref.id;
      }

      const member = {
        id: memberId,
        name: person.name || "",
        role: person.role || "",
        subRole: person.subRole || null,
        ...(isLocal
          ? {}
          : { crossFacility: true, facilityId: sourceFacilityId, facilityName: domainFacilityLabel(sourceFacilityId) }),
        // Day value asked at add time: doctors → call number, CRNAs → leave time.
        ...(isDoctor
          ? (extra ? { callOrder: Number(extra) } : {})
          : (extra ? { shiftCode: extra } : {})),
      };
      // Merge-append onto today's roster (preserves roomOrder/notes/departed).
      await setDailyRoster(
        currentFacilityId,
        date,
        isDoctor
          ? { supervisors: [...(roster.supervisors || []), member] }
          : { crnas: [...(roster.crnas || []), member] },
        actor?.uid || null
      );
      setAddedIds((prev) => [...prev, person.id]);
    } catch (err) {
      console.error("Add cross-facility staff failed:", err);
      // ⚠️ THE MACHINE'S OWN REASON. "Check your connection" sent somebody
      // looking at the wifi for eight days when the real answer was
      // permission-denied — see the note on listAccountsWithoutStaff.
      const code = err?.code || "";
      setLoadError(
        code.includes("permission-denied")
          ? "That needs an administrator account — the directory record could not be created."
          : `Couldn't save${err?.message ? ` — ${err.message}` : "."} `
      );
    } finally {
      setBusyId(null);
    }
  };

  const roleLabel = selectedRole === "anesthesiologist" ? "Doctor" : "CRNA";
  const isDoctorRole = selectedRole === "anesthesiologist";

  const confirmAdd = async () => {
    if (!selected) return;
    await addStaff(selected, sourceFacilityId, extra);
    setSelected(null);
    setExtra("");
  };

  const matches = (p) =>
    (p.name || "").toLowerCase().includes(search.trim().toLowerCase());

  // Phase 5.4 facility rule: floats disabled for this facility (admin bypasses).
  if (!floatAllowed) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Add Staff from Another Facility">
        <div style={{ fontSize: 14, color: color.text.secondary }}>
          Cross-facility floats are turned off for this facility (Setup → Facility → ⚖️ Facility Rules).
          Ask an administrator to enable floats or add the person to this facility.
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Staff from Another Facility"
      maxWidth={520}
      footer={
        <>
          {selectedRole && (
            <Button variant="secondary" onClick={() => { setSelectedRole(null); setStaff([]); setLoadError(""); clearStep2(); }}>
              ← Back
            </Button>
          )}
          <Button variant="secondary" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleClose}>Done</Button>
        </>
      }
    >
      {/* Step 1 — role picker */}
      {!selectedRole && (
        <div>
          <p style={{ ...st.muted, marginBottom: space.md }}>
            Choose the role you need to fill:
          </p>
          <div style={{ display: "flex", gap: space.md }}>
            <button
              style={st.roleBtn}
              onClick={() => pickRole("provider")}
              aria-label="Add a CRNA from another facility"
            >
              <span style={st.roleIcon}>👥</span>
              <span style={st.roleLabel}>CRNA</span>
            </button>
            <button
              style={st.roleBtn}
              onClick={() => pickRole("anesthesiologist")}
              aria-label="Add a Doctor from another facility"
            >
              <span style={st.roleIcon}>🩺</span>
              <span style={st.roleLabel}>Doctor</span>
            </button>
          </div>
        </div>
      )}

      {/* Step 2 — staff list from other facilities */}
      {selectedRole && (
        <div>
          <p style={{ ...st.muted, marginBottom: space.sm }}>
            Showing <strong>{roleLabel}s</strong> not on today's roster (this facility + others).
          </p>

          {loading && <p style={st.muted}>Loading staff…</p>}
          {loadError && <p style={st.error} role="alert">{loadError}</p>}

          {!loading && !loadError && staff.length === 0 && (
            <p style={st.muted}>No available {roleLabel}s left to add.</p>
          )}

          {/* Browse: search + a clean tap-to-pick list (one row per person). */}
          {!loading && staff.length > 0 && !selected && (
            <>
              <input
                style={st.searchInput}
                placeholder={`Search ${roleLabel}s…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label={`Search ${roleLabel}s`}
                autoFocus
              />
              <ul style={st.list}>
                {staff.filter(matches).map((p) => {
                  const added = addedIds.includes(p.id);
                  const rostered = rosteredIds.includes(p.id) && !added;
                  return (
                    <li key={p.id} style={{ listStyle: "none" }}>
                      <button
                        type="button"
                        style={st.pickBtn}
                        disabled={added || rostered}
                        onClick={() => pickPerson(p)}
                      >
                        <span style={st.name}>
                          {p.name}
                          <span style={st.roleBadge}>
                            {p.role === "anesthesiologist" ? "Doctor" : (p.subRole || "CRNA").toUpperCase()}
                          </span>
                          {!p._local && <span style={st.facilityPill}>{domainFacilityLabel(p._foundAtId)}</span>}
                          {/* ⚠️ SAY THAT THIS ONE IS BEING CREATED. Picking an
                              account-only person writes a NEW directory record,
                              which is not what the other rows do — and an
                              accidental one is a duplicate colleague. */}
                          {p._accountOnly && (
                            <span style={st.newPill}>has a login · not in the directory yet</span>
                          )}
                        </span>
                        <span style={st.muted}>{added ? "✓ Added" : rostered ? "On roster" : "Add →"}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {/* Detail: the one person you picked — set facility/leave/call, confirm. */}
          {!loading && selected && (
            <div style={st.detail}>
              <div style={st.detailName}>
                {selected.name}
                <span style={st.roleBadge}>
                  {selected.role === "anesthesiologist" ? "Doctor" : (selected.subRole || "CRNA").toUpperCase()}
                </span>
              </div>
              {!selected._local && (
                <label style={st.label}>
                  From facility
                  <select style={st.select} value={sourceFacilityId} onChange={(e) => setSourceFacilityId(e.target.value)}>
                    {otherFacilities(currentFacilityId).map((f) => (
                      <option key={f.id} value={f.id}>{f.label}</option>
                    ))}
                  </select>
                </label>
              )}
              <label style={st.label}>
                {isDoctorRole ? "Call number" : "Leave time"}
                <select style={st.select} value={extra} onChange={(e) => setExtra(e.target.value)}>
                  <option value="">{isDoctorRole ? "Call #…" : "Leave…"}</option>
                  {(isDoctorRole ? CALL_OPTIONS : SHIFT_OPTIONS).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <div style={{ display: "flex", gap: space.sm, marginTop: space.md }}>
                <Button variant="secondary" onClick={() => setSelected(null)}>← Choose another</Button>
                <Button onClick={confirmAdd} loading={busyId === selected.id}>Add to roster</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

const st = {
  label: {
    display: "block",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    marginBottom: space.xs,
  },
  select: {
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    color: color.text.primary,
    borderRadius: radius.sm,
    padding: "6px 10px",
    fontSize: font.size.label,
    fontFamily: font.family,
    boxSizing: "border-box",
  },
  roleBtn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: space.sm,
    padding: `${space.lg}px ${space.md}px`,
    background: color.surface.card,
    border: `2px solid ${color.border.default}`,
    borderRadius: radius.md,
    cursor: "pointer",
    fontFamily: font.family,
    transition: "border-color .15s",
  },
  roleIcon: {
    fontSize: 32,
  },
  roleLabel: {
    fontSize: font.size.body,
    fontWeight: font.weight.bold,
    color: color.text.primary,
  },
  groupTitle: {
    margin: 0,
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    color: color.text.primary,
    borderRadius: radius.sm,
    padding: "9px 12px",
    fontSize: font.size.body,
    fontFamily: font.family,
    marginBottom: space.sm,
  },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    maxHeight: 320,
    overflowY: "auto",
  },
  pickBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.sm,
    width: "100%",
    minHeight: 44,
    background: "transparent",
    border: "none",
    borderBottom: `1px solid ${color.border.default}`,
    padding: `${space.sm}px 4px`,
    cursor: "pointer",
    fontFamily: font.family,
    textAlign: "left",
  },
  detail: {
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
    padding: space.md,
    background: color.surface.raised,
  },
  detailName: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    fontSize: font.size.heading,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    marginBottom: space.md,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.sm,
    padding: `${space.sm}px 0`,
    borderBottom: `1px solid ${color.border.default}`,
    minHeight: 44,
    flexWrap: "wrap",
  },
  name: {
    display: "inline-flex",
    alignItems: "center",
    gap: space.sm,
    fontSize: font.size.body,
    color: color.text.primary,
  },
  roleBadge: {
    background: color.surface.raised,
    color: color.text.secondary,
    borderRadius: radius.sm,
    padding: "1px 6px",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
  },
  newPill: {
    marginLeft: 6, fontSize: 10, fontWeight: 700,
    color: color.status.closing.text, background: color.status.closing.bg,
    border: `1px solid ${color.status.closing.border}`,
    borderRadius: radius.sm, padding: "1px 6px", whiteSpace: "nowrap",
  },
  facilityPill: {
    background: color.surface.raised,
    color: color.text.secondary,
    borderRadius: radius.full,
    padding: "1px 8px",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
  },
  muted: {
    fontSize: font.size.label,
    color: color.text.muted,
  },
  error: {
    fontSize: font.size.label,
    color: color.accent.error,
  },
};
